import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Phone, FileText, ArrowLeft, CheckCircle, MapPin, Disc3, RefreshCw, Key, KeyRound, Battery, Building2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CookieBanner from '../components/CookieBanner';
import QuoteForm from '../components/QuoteForm';
import Testimonials from '../components/Testimonials';
import { COMPANY, CITIES } from '../constants';
import { useLanguage } from '../i18n/LanguageContext';
import { SLUG_MAP_FR_TO_EN, SLUG_MAP_EN_TO_FR } from '../i18n/translations';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const iconMap = {
  Disc3: Disc3,
  RefreshCw: RefreshCw,
  Key: Key,
  KeyRound: KeyRound,
  Battery: Battery,
  Building2: Building2,
};

const ServiceLandingPage = () => {
  const location = useLocation();
  const { lang, t, services, homePath, altPath } = useLanguage();

  // Extract slug — strip /en/ prefix if present
  const rawPath = location.pathname;
  let slug;
  if (rawPath.startsWith('/en/')) {
    slug = rawPath.substring(4);
  } else {
    slug = rawPath.substring(1);
  }
  const service = services.find(s => s.slug === slug);

  // Build the hreflang alternate URL for this service page
  let frSlug, enSlug;
  if (lang === 'en' && service) {
    enSlug = service.slug;
    frSlug = SLUG_MAP_EN_TO_FR[enSlug];
  } else if (lang === 'fr' && service) {
    frSlug = service.slug;
    enSlug = SLUG_MAP_FR_TO_EN[frSlug];
  }

  useEffect(() => {
    if (service) {
      const origin = window.location.origin;

      // SEO title
      const seoTitle = lang === 'en'
        ? `${service.title} | Reco Pneus — French Riviera`
        : `${service.title} | Reco Pneus - Alpes-Maritimes & Var`;
      document.title = seoTitle;

      // Meta description
      const seoDesc = lang === 'en'
        ? `${service.title} in the Alpes-Maritimes and Var. ${service.shortDesc} Fast response in Cannes, Nice, Antibes, Grasse and surroundings.`
        : `${service.title} dans les Alpes-Maritimes et le Var. ${service.shortDesc} Intervention rapide à Cannes, Nice, Antibes, Grasse et environs.`;

      document.documentElement.lang = lang;

      // Helper to set or create a meta tag
      const setMeta = (attr, key, content) => {
        let el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
        el.content = content;
      };

      setMeta('name', 'description', seoDesc);
      setMeta('property', 'og:title', seoTitle);
      setMeta('property', 'og:description', seoDesc);
      setMeta('property', 'og:url', origin + location.pathname);
      setMeta('property', 'og:locale', lang === 'en' ? 'en_GB' : 'fr_FR');
      setMeta('name', 'twitter:title', seoTitle);
      setMeta('name', 'twitter:description', seoDesc);

      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
      canonical.href = origin + location.pathname;

      // Hreflang tags
      document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
      if (frSlug && enSlug) {
        const addHreflang = (hreflang, href) => {
          const link = document.createElement('link');
          link.rel = 'alternate';
          link.hreflang = hreflang;
          link.href = href;
          document.head.appendChild(link);
        };
        addHreflang('fr', `${origin}/${frSlug}`);
        addHreflang('en', `${origin}/en/${enSlug}`);
        addHreflang('x-default', `${origin}/${frSlug}`);
      }
    }
    window.scrollTo(0, 0);

    return () => {
      document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, [service, lang, frSlug, enSlug, location.pathname]);

  if (!service) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t.servicePage.notFound}</h1>
          <Link to={homePath}>
            <Button className="bg-[#41DB23] text-white hover:bg-[#38C41E]">
              {t.servicePage.notFoundBtn}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon];

  const handleScrollToDevis = (e) => {
    e.preventDefault();
    const element = document.getElementById('devis');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`service-page-${service.id}`}>
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-16 sm:py-24 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link
              to={homePath}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#41DB23] text-sm mb-8 transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.servicePage.backHome}
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                {/* Service Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#41DB23]/10 border border-[#41DB23]/30 rounded-full mb-6">
                  {IconComponent && <IconComponent className="w-4 h-4 text-[#41DB23]" />}
                  <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#41DB23]">
                    {t.servicePage.serviceBadge}
                  </span>
                </div>

                {/* Title */}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tighter text-gray-900 mb-6 leading-tight">
                  {service.title}
                </h1>

                {/* Description */}
                <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed">
                  {service.shortDesc}
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={COMPANY.phoneLink} data-testid="service-hero-phone">
                    <Button
                      size="lg"
                      className="w-full sm:w-auto bg-[#41DB23] text-white hover:bg-[#38C41E] font-bold uppercase tracking-wide px-6 py-4 text-base rounded-md flex items-center justify-center gap-3 shadow-lg"
                    >
                      <Phone className="w-5 h-5" />
                      {t.callNow}
                    </Button>
                  </a>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={handleScrollToDevis}
                    className="w-full sm:w-auto border-2 border-[#41DB23] text-[#41DB23] hover:bg-[#41DB23]/10 font-bold uppercase tracking-wide px-6 py-4 text-base rounded-md flex items-center justify-center gap-3"
                    data-testid="service-hero-devis"
                  >
                    <FileText className="w-5 h-5" />
                    {t.hero.ctaQuote}
                  </Button>
                </div>
              </div>

              {/* Benefits Card */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  {t.servicePage.whyChoose}
                </h2>
                <ul className="space-y-4">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#41DB23] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Coverage Area Section */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#41DB23] mb-4 block">
                {t.servicePage.coverageBadge}
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-gray-900 mb-4">
                {t.servicePage.coverageTitle}
              </h2>
              <p className="text-base text-gray-600 max-w-2xl mx-auto">
                {t.servicePage.coverageDesc(service.title)}
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {CITIES.map((city) => (
                <span
                  key={city}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-600 hover:border-[#41DB23]/50 hover:text-gray-900 transition-colors"
                >
                  <MapPin className="w-3 h-3 text-[#41DB23]" />
                  {city}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 sm:py-24 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#41DB23] mb-4 block">
                {t.servicePage.faqBadge}
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-gray-900">
                {t.servicePage.faqTitle}
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {service.faq.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-white border border-gray-200 rounded-xl px-6 data-[state=open]:border-[#41DB23]/50 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-gray-900 hover:text-[#41DB23] py-4 text-base font-medium">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Schema.org FAQPage */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": service.faq.map(item => ({
                "@type": "Question",
                "name": item.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": item.answer
                }
              }))
            })
          }} />
        </section>

        {/* Testimonials */}
        <Testimonials />

        {/* Quote Form */}
        <QuoteForm />

        {/* Final CTA */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-gray-900 mb-4">
              {t.servicePage.finalCtaTitle(service.title)}
            </h2>
            <p className="text-gray-600 mb-8">
              {t.servicePage.finalCtaDesc}
            </p>
            <a href={COMPANY.phoneLink} data-testid="service-final-cta">
              <Button
                size="lg"
                className="bg-[#41DB23] text-white hover:bg-[#38C41E] font-bold uppercase tracking-wide px-8 py-6 text-lg rounded-md inline-flex items-center gap-3 shadow-lg animate-pulse-glow"
              >
                <Phone className="w-6 h-6" />
                {COMPANY.phone}
              </Button>
            </a>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />

      {/* Schema.org Service */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": service.title,
          "description": service.shortDesc,
          "provider": {
            "@type": "LocalBusiness",
            "name": "Reco Pneus",
            "telephone": "+33423340634"
          },
          "areaServed": ["Alpes-Maritimes", "Var", "Cannes", "Mandelieu-la-Napoule", "Antibes", "Nice"]
        })
      }} />
    </div>
  );
};

export default ServiceLandingPage;
